import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

/* =======================
   PAGE COMPONENT
======================= */

const PrivacyPolicy = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState("all"); // expand all by default

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const toggle = (i) => {
    if (openIndex === "all") {
      setOpenIndex(i);
    } else {
      setOpenIndex(openIndex === i ? null : i);
    }
  };

  return (
    <>
      {/* -------- TOP NAV -------- */}
      <TopNav>
        <NavInner>
          <Brand to="/">RechargeJunction</Brand>

          <NavLinks>
            <Link to="/">Home</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
            <Link to="/contact">Contact</Link>
          </NavLinks>

          <Burger onClick={() => setMenuOpen(true)}>
            <FaBars />
          </Burger>
        </NavInner>
      </TopNav>

      {/* -------- MOBILE MENU -------- */}
      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <Close onClick={() => setMenuOpen(false)}>
              <FaTimes />
            </Close>

            <MobileLinks>
              <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>Register</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
            </MobileLinks>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* -------- PAGE CONTENT -------- */}
      <Page>
        <Container>
          <Title>Privacy Policy</Title>
          <Subtitle>
            How we collect, use & protect your personal information
          </Subtitle>

          <Timeline>
            {SECTIONS.map((item, i) => (
              <TimelineItem key={i}>
                <Dot />
                <TimelineTitle>{item.title}</TimelineTitle>

                <Accordion>
                  <AccordionHeader onClick={() => toggle(i)}>
                    {item.subtitle}
                    <Icon
                      animate={{
                        rotate:
                          openIndex === "all" || openIndex === i ? 180 : 0,
                      }}
                    >
                      <FaChevronDown />
                    </Icon>
                  </AccordionHeader>

                  <AnimatePresence>
                    {(openIndex === "all" || openIndex === i) && (
                      <AccordionContent
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                      >
                        {item.content}
                      </AccordionContent>
                    )}
                  </AnimatePresence>
                </Accordion>
              </TimelineItem>
            ))}
          </Timeline>
        </Container>
      </Page>
    </>
  );
};

export default PrivacyPolicy;

/* =======================
   CONTENT DATA
======================= */

const SECTIONS = [
  {
    title: "Introduction",
    subtitle: "Overview & acceptance",
    content: (
      <>
        <p>
          This Privacy Policy describes how <b>Recharge Junction</b> collects,
          stores, uses and protects your personal information.
        </p>
        <p>
          By visiting or using Recharge Junction, you expressly consent to the
          collection and use of your data in accordance with this policy.
        </p>
        <p>
          If you do not agree with this policy, please do not use or access the
          website.
        </p>
      </>
    ),
  },
  {
    title: "Policy Updates",
    subtitle: "Changes & applicability",
    content: (
      <p>
        Our Privacy Policy may change at any time without prior notice. You are
        advised to review this policy periodically. This Privacy Policy applies
        uniformly to all Recharge Junction services and platforms.
      </p>
    ),
  },
  {
    title: "General Data Practices",
    subtitle: "How we handle your data",
    content: (
      <>
        <p>
          We do not sell, share or rent your personal information to third
          parties, nor do we use your email or mobile number for unsolicited
          communication.
        </p>
        <p>
          Communications sent by Recharge Junction are strictly related to
          services, transactions or policy updates.
        </p>
        <p>
          We may share aggregated, non-identifiable statistical data such as
          visitor counts or transaction volumes.
        </p>
        <p>
          We reserve the right to disclose personal information when legally
          required by law enforcement or government authorities.
        </p>
      </>
    ),
  },
  {
    title: "Personal Information",
    subtitle: "What information we collect",
    content: (
      <>
        <p>
          Personal Information includes data that can identify an individual,
          such as name, address, email ID, phone number, payment details, mobile
          or DTH service information, electricity connection details and other
          data voluntarily provided by users.
        </p>
        <p>
          While browsing Recharge Junction, we may collect information such as
          IP address, browser type, ISP, domain, and anonymous usage statistics.
        </p>
      </>
    ),
  },
  {
    title: "Use of Personal Information",
    subtitle: "Why we collect your data",
    content: (
      <>
        <p>
          We use personal information to provide requested services, resolve
          disputes, process payments, troubleshoot issues, prevent fraud and
          enforce our terms and conditions.
        </p>
        <p>
          We may use your contact information to notify you about offers,
          services or updates relevant to your usage history.
        </p>
        <p>
          Optional surveys may be conducted to better understand user
          preferences and improve service experience.
        </p>
      </>
    ),
  },
  {
    title: "Log Data",
    subtitle: "Technical & diagnostic information",
    content: (
      <p>
        In case of errors within our application, we may collect log data via
        third-party tools. This includes IP address, device name, OS version,
        app configuration, date & time of usage and diagnostic statistics. We do
        not store or upload contact data.
      </p>
    ),
  },
  {
    title: "Cookies",
    subtitle: "Tracking technologies",
    content: (
      <p>
        Cookies are small data files used to enhance user experience. Recharge
        Junction uses cookies and tracking technologies where required. No
        personal data is collected through cookies unless explicitly provided
        earlier. Aggregated cookie data may be shared with partners.
      </p>
    ),
  },
  {
    title: "External Links",
    subtitle: "Third-party websites",
    content: (
      <p>
        Recharge Junction may contain links to external websites. We are not
        responsible for the privacy practices or content of such third-party
        sites.
      </p>
    ),
  },
  {
    title: "Security",
    subtitle: "Protection of information",
    content: (
      <p>
        Recharge Junction employs stringent security measures to protect your
        data from loss, misuse or unauthorized access. Secure servers are used
        whenever sensitive account information is accessed or modified.
      </p>
    ),
  },
  {
    title: "Consent",
    subtitle: "User agreement",
    content: (
      <p>
        By using Recharge Junction or providing your information, you consent to
        the collection, use and sharing of your data in accordance with this
        Privacy Policy.
      </p>
    ),
  },
];

/* =======================
   STYLES
======================= */

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(8, 10, 16, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NavInner = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Brand = styled(Link)`
  font-size: 18px;
  font-weight: 700;
  color: #ffb97b;
  text-decoration: none;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 22px;

  a {
    color: #ddd;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #ffb97b;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.div`
  display: none;
  font-size: 22px;
  color: #fff;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: #080a10;
  z-index: 200;
  padding: 90px 30px;
`;

const Close = styled.div`
  position: absolute;
  top: 22px;
  right: 22px;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  a {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;

    &:hover {
      color: #ffb97b;
    }
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #141823, #080a10);
  padding: 140px 20px 90px;
  color: #ddd;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 42px;
  text-align: center;
  margin-bottom: 12px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  opacity: 0.85;
  margin-bottom: 70px;
  color: #d6d6d6;
`;

const Timeline = styled.div`
  position: relative;
  margin-left: 25px;
  border-left: 2px solid rgba(255, 255, 255, 0.15);
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 50px;
  padding-left: 40px;
`;

const Dot = styled.div`
  position: absolute;
  left: -11px;
  top: 6px;
  width: 18px;
  height: 18px;
  background: #ffb97b;
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(255, 185, 123, 0.6);
`;

const TimelineTitle = styled.h3`
  font-size: 22px;
  margin-bottom: 12px;
  color: #ffcc9c;
`;

const Accordion = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  overflow: hidden;
`;

const AccordionHeader = styled.div`
  padding: 18px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.03);
  color: #ffffff;
  cursor: pointer;
`;

const AccordionContent = styled(motion.div)`
  padding: 0 22px 22px;
  font-size: 15px;
  line-height: 1.7;
  opacity: 0.95;
  color: #ddd;
`;

const Icon = styled(motion.span)`
  display: flex;
  align-items: center;
`;